/**
 * API Utility Functions
 *
 * Utility functions for API operations.
 *
 * @since 1.0.0
 */

import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';

import type { GlobalTab } from '../components/global-tabs/types';
import type { ResetToDefaultsResponse, SaveTabsResponse } from './types';

/**
 * Save global tabs to API
 *
 * @since 1.0.0
 *
 * @param tabs Tabs to save.
 *
 * @return Promise with response data.
 */
export const saveGlobalTabs = async (tabs: GlobalTab[]): Promise<SaveTabsResponse> => {
  // Update priorities based on current order using (10 * index) + 10
  const tabsWithUpdatedPriorities = tabs.map((tab, index) => ({
    ...tab,
    priority: 10 * index + 10,
  }));

  const response = await apiFetch<SaveTabsResponse>({
    path: '/redfox-tab-manager/v1/global-tabs',
    method: 'POST',
    data: { tabs: tabsWithUpdatedPriorities },
  });

  return {
    success: true,
    message: response?.message ?? __('Global tabs saved successfully!', 'redfox-tab-manager'),
  };
};

/**
 * Reset global tabs to defaults via API
 *
 * @since 1.0.0
 *
 * @return Promise with response data and default tabs.
 */
export const resetGlobalTabsToDefaults = async (): Promise<ResetToDefaultsResponse> => {
  const response = await apiFetch<{ success: boolean; message: string; data: GlobalTab[] }>({
    path: '/redfox-tab-manager/v1/global-tabs/reset-to-defaults',
    method: 'POST',
  });

  return {
    success: true,
    message:
      response?.message ?? __('Global tabs reset to defaults successfully!', 'redfox-tab-manager'),
    data: response?.data ?? [],
  };
};
